import React from 'react';
import type { HorseSocialBehavior } from '../../../../types/horseProfile';

interface SocialBehaviorSectionProps {
  behavior: HorseSocialBehavior;
}

const DEFAULT_BEHAVIOR: HorseSocialBehavior = {
  temperament: [],
  socialWithHorses: 'good',
  socialWithHumans: 'good',
  handlingNotes: '',
  preferences: [],
  behavioralIssues: []
};

export const SocialBehaviorSection: React.FC<SocialBehaviorSectionProps> = ({ behavior = DEFAULT_BEHAVIOR }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Temperament</h4>
        <div className="flex flex-wrap gap-2">
          {behavior.temperament?.map((trait, index) => (
            <span
              key={index}
              className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {trait}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sozialverhalten mit Pferden</h4>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                behavior.socialWithHorses === 'excellent' ? 'bg-green-500' :
                behavior.socialWithHorses === 'good' ? 'bg-blue-500' :
                behavior.socialWithHorses === 'fair' ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {behavior.socialWithHorses === 'excellent' ? 'Ausgezeichnet' :
                 behavior.socialWithHorses === 'good' ? 'Gut' :
                 behavior.socialWithHorses === 'fair' ? 'Mittelmäßig' :
                 'Schwierig'}
              </span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sozialverhalten mit Menschen</h4>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                behavior.socialWithHumans === 'excellent' ? 'bg-green-500' :
                behavior.socialWithHumans === 'good' ? 'bg-blue-500' :
                behavior.socialWithHumans === 'fair' ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {behavior.socialWithHumans === 'excellent' ? 'Ausgezeichnet' :
                 behavior.socialWithHumans === 'good' ? 'Gut' :
                 behavior.socialWithHumans === 'fair' ? 'Mittelmäßig' :
                 'Schwierig'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {behavior.handlingNotes && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Umgangshinweise</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            {behavior.handlingNotes}
          </p>
        </div>
      )}

      {behavior.preferences?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Vorlieben & Abneigungen</h4>
          <div className="space-y-2">
            {behavior.preferences.map((pref, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">{pref.category}</h5>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{pref.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {behavior.behavioralIssues?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Verhaltensauffälligkeiten</h4>
          <ul className="list-disc list-inside space-y-1">
            {behavior.behavioralIssues.map((issue, index) => (
              <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};